import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Calendar, Clock, MapPin, Users, Zap, Ticket, CheckCircle2, AlertTriangle, Smartphone, Loader2, Home, QrCode, X, Eraser } from "lucide-react";
import SignatureCanvas from "react-signature-canvas";
import apiClient, { getErrorMessage } from "@/lib/apiClient";
import { DEFAULT_TZ, tzLabel } from "@/lib/timezones";
import logo from "@/assets/logo.png";

const statusStyle = (status) => {
  const map = {
    confirmed: { color: "#00ff9d", bg: "rgba(0,255,157,0.12)" },
    pending: { color: "#ffb020", bg: "rgba(255,176,32,0.12)" },
    active: { color: "#00e5ff", bg: "rgba(0,229,255,0.12)" },
    cancelled: { color: "#ff2e97", bg: "rgba(255,46,151,0.12)" },
    completed: { color: "#94a3b8", bg: "rgba(148,163,184,0.12)" },
  };
  return map[status] || map.pending;
};

const formatDuration = (start, end) => {
  const total = Math.round((new Date(end).getTime() - new Date(start).getTime()) / 60000);
  const h = Math.floor(total / 60);
  const m = total % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
};

const Row = ({ icon, label, value }) => (
  <div className="flex items-start gap-3 py-2">
    <div className="mt-0.5">{icon}</div>
    <div className="flex-1 min-w-0">
      <p className="text-slate-500 text-xs uppercase tracking-wider">{label}</p>
      <p className="text-white break-words">{value}</p>
    </div>
  </div>
);

function CheckinModal({ booking, onClose }) {
  const [step, setStep] = useState("email"); // email | sign | working | done | error
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState(booking.guest_name || "");
  const [phone, setPhone] = useState("");
  const [waiver, setWaiver] = useState(null);
  const [msg, setMsg] = useState("");
  const [session, setSession] = useState(null);
  const sigRef = useRef(null);

  const submitEmail = async (e) => {
    e?.preventDefault();
    const clean = email.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(clean)) { setMsg("Enter the email you booked with."); return; }
    setStep("working"); setMsg("");
    try {
      const { data } = await apiClient.post(`/checkin/booking/${booking.id}`, { email: clean });
      if (data.checked_in) { setSession(data.session); setStep("done"); return; }
      // Needs a waiver — load its content and show the signer.
      const w = await apiClient.get("/waiver/current");
      if (!w.data?.has_waiver) { setMsg("No active waiver — please see the front desk."); setStep("error"); return; }
      setWaiver(w.data);
      setStep("sign");
    } catch (err) { setMsg(getErrorMessage(err, "Check-in failed")); setStep("error"); }
  };

  const submitSign = async () => {
    if (!fullName.trim()) { setMsg("Enter your full legal name."); return; }
    if (!sigRef.current || sigRef.current.isEmpty()) { setMsg("Please draw your signature."); return; }
    const dataUrl = sigRef.current.getCanvas().toDataURL("image/png");
    setStep("working"); setMsg("");
    try {
      await apiClient.post("/waiver/sign", {
        version_id: waiver.id, full_name: fullName.trim(), email: email.trim().toLowerCase(),
        signature_data_url: dataUrl, phone: phone.trim() || null, booking_id: booking.id,
      });
      const { data } = await apiClient.post(`/checkin/booking/${booking.id}`, { email: email.trim().toLowerCase() });
      setSession(data.session); setStep("done");
    } catch (err) { setMsg(getErrorMessage(err, "Could not complete check-in")); setStep("error"); }
  };

  const waiverText = (waiver?.body_html || "")
    .replace(/<br\s*\/?>/gi, "\n").replace(/<\/p>/gi, "\n\n").replace(/<\/li>/gi, "\n")
    .replace(/<li[^>]*>/gi, "• ").replace(/<[^>]+>/g, "").trim();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm px-4 py-6" data-testid="checkin-modal">
      <div className="w-full max-w-md glass rounded-3xl p-6 md:p-8 relative max-h-[90vh] overflow-y-auto">
        <button onClick={onClose} className="absolute top-4 right-4 text-slate-400 hover:text-white" data-testid="checkin-close"><X className="w-5 h-5" /></button>

        {step === "email" && (
          <form onSubmit={submitEmail}>
            <QrCode className="w-10 h-10 mb-3" style={{ color: "#00e5ff" }} />
            <h2 className="font-display text-xl font-bold text-white mb-1">Check in</h2>
            <p className="text-slate-400 text-sm mb-5">Confirm the email you booked with. We&apos;ll get your waiver on file and start your check-in.</p>
            <label className="text-slate-400 text-xs uppercase tracking-wider">Email</label>
            <input autoFocus type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              className="w-full mt-1 mb-2 bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-white outline-none focus:border-cyan-400"
              placeholder="you@email.com" data-testid="checkin-email-input" />
            {msg && <p className="text-pink-400 text-sm mb-2">{msg}</p>}
            <button type="submit" className="neon-btn w-full py-3 px-6 gap-3 mt-3" data-testid="checkin-email-submit">Continue</button>
          </form>
        )}

        {step === "sign" && (
          <div>
            <h2 className="font-display text-xl font-bold text-white mb-1">{waiver?.title || "Liability Waiver"}</h2>
            <p className="text-slate-500 text-xs mb-3">v{waiver?.version}</p>
            <div className="bg-black/30 border border-white/10 rounded-xl p-3 mb-4 max-h-40 overflow-y-auto whitespace-pre-wrap text-slate-300 text-xs leading-relaxed">{waiverText}</div>
            <label className="text-slate-400 text-xs uppercase tracking-wider">Full legal name</label>
            <input value={fullName} onChange={(e) => setFullName(e.target.value)}
              className="w-full mt-1 mb-3 bg-black/40 border border-white/10 rounded-xl px-4 py-2.5 text-white outline-none focus:border-cyan-400"
              placeholder="Jane Q. Public" data-testid="checkin-name-input" />
            <label className="text-slate-400 text-xs uppercase tracking-wider">Signature</label>
            <div className="mt-1 rounded-xl overflow-hidden border border-white/10 bg-white">
              <SignatureCanvas ref={sigRef} penColor="#0b0f17" canvasProps={{ className: "w-full", height: 160, "data-testid": "checkin-signature" }} />
            </div>
            <button onClick={() => sigRef.current?.clear()} className="flex items-center gap-1.5 text-cyan-400 text-xs mt-2 ml-auto" data-testid="checkin-clear"><Eraser className="w-3.5 h-3.5" /> Clear</button>
            {msg && <p className="text-pink-400 text-sm mt-2">{msg}</p>}
            <button onClick={submitSign} className="neon-btn w-full py-3 px-6 gap-3 mt-4" data-testid="checkin-sign-submit"><CheckCircle2 className="w-4 h-4" /> Agree &amp; Check In</button>
          </div>
        )}

        {step === "working" && (
          <div className="py-12 text-center">
            <Loader2 className="w-12 h-12 mx-auto animate-spin mb-3" style={{ color: "#00e5ff" }} />
            <p className="text-white">Working…</p>
          </div>
        )}

        {step === "done" && (
          <div className="py-6 text-center" data-testid="checkin-done">
            <CheckCircle2 className="w-16 h-16 mx-auto mb-3" style={{ color: "#00ff9d" }} />
            <h2 className="font-display text-2xl font-bold text-white mb-2">You&apos;re checked in!</h2>
            <p className="text-slate-400 text-sm mb-5">{session?.station_name ? `Station: ${session.station_name}. ` : ""}Head to your station and scan its QR to start your session.</p>
            <button onClick={onClose} className="neon-outline-btn w-full py-3 px-6">Done</button>
          </div>
        )}

        {step === "error" && (
          <div className="py-6 text-center">
            <AlertTriangle className="w-14 h-14 mx-auto mb-3" style={{ color: "#ffb020" }} />
            <p className="text-slate-300 text-sm mb-5">{msg}</p>
            <button onClick={() => { setMsg(""); setStep("email"); }} className="neon-outline-btn w-full py-3 px-6" data-testid="checkin-retry">Try again</button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function PublicBooking() {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCheckin, setShowCheckin] = useState(false);

  useEffect(() => {
    apiClient.get(`/public/bookings/${bookingId}`)
      .then(({ data }) => setBooking(data))
      .catch((e) => setError(e?.response?.status === 404 ? "This booking link is invalid or has been removed." : "Could not load booking. Please try again."))
      .finally(() => setLoading(false));
  }, [bookingId]);

  const openInApp = () => {
    // Try the app's custom scheme. If the app isn't installed, the browser will silently ignore it.
    window.location.href = `pitlanelounge:///booking/${bookingId}`;
  };

  if (loading) return (
    <div className="min-h-screen grid-overlay flex items-center justify-center px-5">
      <Loader2 className="w-10 h-10 animate-spin" style={{ color: "#00e5ff" }} />
    </div>
  );

  if (error || !booking) return (
    <div className="min-h-screen grid-overlay flex items-center justify-center px-5">
      <div className="w-full max-w-md glass rounded-3xl p-10 text-center">
        <img src={logo} alt="logo" className="h-12 w-auto object-contain mx-auto mb-6" />
        <AlertTriangle className="w-14 h-14 mx-auto mb-4" style={{ color: "#ffb020" }} />
        <h1 className="font-display text-xl font-bold text-white mb-2">Booking not found</h1>
        <p className="text-slate-400 mb-6">{error || "This booking link is invalid or has been removed."}</p>
        <button onClick={() => navigate("/")} className="neon-outline-btn w-full py-3 px-6 gap-3"><Home className="w-4 h-4" /> Back to Home</button>
      </div>
    </div>
  );

  const s = statusStyle(booking.status);
  const isPending = booking.status === "pending" && (booking.total_amount || 0) > 0;
  const stationText = (booking.station_names || []).join(", ") || "Station";

  return (
    <div className="min-h-screen grid-overlay flex items-center justify-center px-5 py-10">
      <div className="w-full max-w-lg glass rounded-3xl p-8 md:p-10 animate-in-up">
        <div className="flex items-center justify-between mb-6">
          <img src={logo} alt="Pit Lane Lounge" className="h-10 w-auto object-contain" />
          <span className="chip capitalize" style={{ background: s.bg, border: `1px solid ${s.color}66`, color: s.color }}>{booking.status}</span>
        </div>

        <div className="text-center mb-6">
          {booking.status === "confirmed" ? (
            <CheckCircle2 className="w-12 h-12 mx-auto mb-3" style={{ color: "#00ff9d" }} />
          ) : (
            <Ticket className="w-12 h-12 mx-auto mb-3" style={{ color: "#00e5ff" }} />
          )}
          <h1 className="font-display text-2xl font-bold text-white mb-1">
            {booking.is_guest ? `Hi ${(booking.guest_name || "Racer").split(" ")[0]}!` : "Your Booking"}
          </h1>
          <p className="text-slate-500 text-xs uppercase tracking-widest">#{(booking.id || "").slice(-8).toUpperCase()}</p>
        </div>

        {isPending && (
          <div className="rounded-xl p-3 mb-6 flex items-start gap-3" style={{ background: "rgba(255,176,32,0.10)", border: "1px solid rgba(255,176,32,0.4)" }}>
            <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: "#ffb020" }} />
            <div className="text-sm">
              <p className="text-slate-100 font-semibold">Payment pending</p>
              <p className="text-slate-400">Your slot is on hold. Complete payment from the confirmation link we sent by email.</p>
            </div>
          </div>
        )}

        <div className="space-y-1 border-t border-white/5 pt-4">
          <Row icon={<Calendar className="w-4 h-4 text-slate-500" />} label="Date" value={new Date(booking.start_time).toLocaleDateString(undefined, { weekday: "long", year: "numeric", month: "long", day: "numeric", timeZone: DEFAULT_TZ })} />
          <Row icon={<Clock className="w-4 h-4 text-slate-500" />} label="Time" value={`${new Date(booking.start_time).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", timeZone: DEFAULT_TZ })} – ${new Date(booking.end_time).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", timeZone: DEFAULT_TZ })} · ${formatDuration(booking.start_time, booking.end_time)}`} />
          <Row icon={<MapPin className="w-4 h-4 text-slate-500" />} label={(booking.station_names || []).length > 1 ? `${booking.station_names.length} Stations` : "Station"} value={stationText} />
          <Row icon={<Users className="w-4 h-4 text-slate-500" />} label="Party size" value={`${booking.party_size} player${booking.party_size === 1 ? "" : "s"}`} />
          {booking.motion_selected && <Row icon={<Zap className="w-4 h-4" style={{ color: "#ff2e97" }} />} label="Motion add-on" value="Yes" />}
          {booking.special_requests && <Row icon={<Ticket className="w-4 h-4 text-slate-500" />} label="Notes" value={booking.special_requests} />}
        </div>

        <div className="border-t border-white/10 mt-5 pt-5 mb-6 flex justify-between text-sm">
          <span className="text-slate-300">Total</span>
          <span className="font-display font-bold text-lg neon-cyan-text">${(booking.total_amount || 0).toFixed(2)}</span>
        </div>

        <p className="text-slate-500 text-xs mb-5 flex items-center gap-1.5" data-testid="atlantic-time-note">
          <Clock className="w-3.5 h-3.5 shrink-0" /> All times shown in {tzLabel(DEFAULT_TZ)} — the lounge&apos;s local time.
        </p>

        <div className="space-y-3">
          {booking.status === "confirmed" && (
            <button onClick={() => setShowCheckin(true)} className="neon-btn w-full py-4 px-6 gap-3" data-testid="public-checkin-btn">
              <QrCode className="w-5 h-5 shrink-0" /> <span>Check In &amp; Sign Waiver</span>
            </button>
          )}
          <button onClick={openInApp} className={`w-full py-4 px-6 gap-3 ${booking.status === "confirmed" ? "neon-outline-btn" : "neon-btn"}`} data-testid="open-in-app">
            <Smartphone className="w-5 h-5 shrink-0" /> <span>Open in the App</span>
          </button>
          {booking.is_guest && (
            <button onClick={() => navigate("/register")} className="neon-outline-btn w-full py-4 px-6 gap-3" data-testid="public-create-account">
              <Ticket className="w-5 h-5 shrink-0" /> <span>Create Account &amp; Earn Points</span>
            </button>
          )}
          {!booking.is_guest && (
            <button onClick={() => navigate("/my-bookings")} className="neon-outline-btn w-full py-4 px-6 gap-3">
              <Ticket className="w-5 h-5 shrink-0" /> <span>View All My Bookings</span>
            </button>
          )}
        </div>

        <p className="text-slate-600 text-xs text-center mt-6">
          Save this link — you can come back to view your booking anytime.
        </p>
      </div>
      {showCheckin && <CheckinModal booking={booking} onClose={() => setShowCheckin(false)} />}
    </div>
  );
}
